// Sounds, confetti, and number animation. All synthesized in-page: no audio
// files, no network. Sound only ever starts from a user gesture (browser
// autoplay rules require it anyway), and the mute toggle persists per device.

import type { Band } from "../shared/economy";

const SOUND_KEY = "liftoff.sound";

export function soundEnabled(): boolean {
  return localStorage.getItem(SOUND_KEY) !== "off";
}

export function setSoundEnabled(enabled: boolean): void {
  localStorage.setItem(SOUND_KEY, enabled ? "on" : "off");
}

let audioContext: AudioContext | null = null;

function context(): AudioContext | null {
  if (!soundEnabled()) return null;
  try {
    audioContext ??= new AudioContext();
    if (audioContext.state === "suspended") void audioContext.resume();
    return audioContext;
  } catch {
    return null;
  }
}

interface Tone {
  frequency: number;
  start: number;      // seconds from now
  duration: number;
  type?: OscillatorType;
  volume?: number;
}

function play(tones: Tone[]): void {
  const ctx = context();
  if (!ctx) return;
  const now = ctx.currentTime;
  for (const tone of tones) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = tone.type ?? "sine";
    osc.frequency.setValueAtTime(tone.frequency, now + tone.start);
    const volume = tone.volume ?? 0.12;
    gain.gain.setValueAtTime(0, now + tone.start);
    gain.gain.linearRampToValueAtTime(volume, now + tone.start + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + tone.start + tone.duration);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now + tone.start);
    osc.stop(now + tone.start + tone.duration + 0.05);
  }
}

// Older bands get fuller, brighter sounds; teens get short, low-key ticks.
function intensity(band: Band): number {
  if (band === "A" || band === "B") return 1;
  if (band === "C") return 0.85;
  if (band === "D") return 0.6;
  return 0.4;
}

export const sounds = {
  tap(band: Band): void {
    play([{ frequency: 660, start: 0, duration: 0.06, type: "triangle", volume: 0.06 * intensity(band) }]);
  },
  fuelGain(band: Band, amount: number): void {
    const steps = Math.min(5, Math.max(2, Math.round(amount / 2)));
    play(Array.from({ length: steps }, (_, index) => ({
      frequency: 520 + index * 90,
      start: index * 0.07,
      duration: 0.09,
      type: "triangle" as OscillatorType,
      volume: 0.09 * intensity(band),
    })));
  },
  milestone(band: Band): void {
    play([
      { frequency: 523, start: 0, duration: 0.12, volume: 0.1 * intensity(band) },
      { frequency: 659, start: 0.1, duration: 0.16, volume: 0.1 * intensity(band) },
    ]);
  },
  unlock(band: Band): void {
    play([
      { frequency: 523, start: 0, duration: 0.14, volume: 0.12 * intensity(band) },
      { frequency: 659, start: 0.11, duration: 0.14, volume: 0.12 * intensity(band) },
      { frequency: 784, start: 0.22, duration: 0.22, volume: 0.13 * intensity(band) },
      { frequency: 1047, start: 0.34, duration: 0.3, volume: 0.1 * intensity(band) },
    ]);
  },
  liftoff(band: Band): void {
    const ctx = context();
    if (!ctx) return;
    // Rumble: filtered noise burst
    const now = ctx.currentTime;
    const buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let index = 0; index < data.length; index += 1) data[index] = (Math.random() * 2 - 1) * (1 - index / data.length);
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(220, now);
    filter.frequency.exponentialRampToValueAtTime(90, now + 1);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.22 * intensity(band), now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.4);
    noise.connect(filter).connect(gain).connect(ctx.destination);
    noise.start(now);
    // Rising whoosh + triumphant fifth
    play([
      { frequency: 196, start: 0.15, duration: 0.9, type: "sawtooth", volume: 0.05 * intensity(band) },
      { frequency: 523, start: 0.9, duration: 0.35, volume: 0.12 * intensity(band) },
      { frequency: 784, start: 1.1, duration: 0.5, volume: 0.12 * intensity(band) },
    ]);
  },
};

const CONFETTI_COLORS = ["#22a566", "#ffcc56", "#ff6b3d", "#47a6ff", "#a78bfa"];

export function burstConfetti(origin?: { x: number; y: number }, count = 28): void {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const host = document.createElement("div");
  host.className = "confetti-host";
  const x = origin?.x ?? window.innerWidth / 2;
  const y = origin?.y ?? window.innerHeight / 3;
  for (let index = 0; index < count; index += 1) {
    const piece = document.createElement("i");
    piece.style.left = `${x}px`;
    piece.style.top = `${y}px`;
    piece.style.background = CONFETTI_COLORS[index % CONFETTI_COLORS.length] ?? "#22a566";
    piece.style.setProperty("--dx", `${(Math.random() - 0.5) * 340}px`);
    piece.style.setProperty("--dy", `${-40 - Math.random() * 300}px`);
    piece.style.setProperty("--rot", `${Math.random() * 720 - 360}deg`);
    piece.style.animationDelay = `${Math.random() * 0.15}s`;
    host.appendChild(piece);
  }
  document.body.appendChild(host);
  window.setTimeout(() => host.remove(), 2200);
}

// Animates a number from its previous value; returns a cleanup function.
export function countUp(element: HTMLElement | null, from: number, to: number, durationMs = 800): () => void {
  if (!element || from === to) {
    if (element) element.textContent = to.toLocaleString();
    return () => {};
  }
  const start = performance.now();
  let frame = 0;
  const step = (time: number) => {
    const progress = Math.min(1, (time - start) / durationMs);
    const eased = 1 - (1 - progress) ** 3;
    element.textContent = Math.round(from + (to - from) * eased).toLocaleString();
    if (progress < 1) frame = requestAnimationFrame(step);
  };
  frame = requestAnimationFrame(step);
  return () => cancelAnimationFrame(frame);
}

// Local memory for celebration states so we cheer once, not on every render.
export function celebrationMemory(studentId: string): {
  spendable: number | null;
  unlockedSeen: string[];
  quarterSeen: Record<string, number>;
  save: (next: { spendable: number; unlockedSeen: string[]; quarterSeen: Record<string, number> }) => void;
} {
  const key = `liftoff.celebrate.${studentId}`;
  let parsed: { spendable: number | null; unlockedSeen: string[]; quarterSeen: Record<string, number> };
  try {
    parsed = JSON.parse(localStorage.getItem(key) ?? "") as typeof parsed;
    if (!Array.isArray(parsed.unlockedSeen)) throw new Error("bad shape");
  } catch {
    parsed = { spendable: null, unlockedSeen: [], quarterSeen: {} };
  }
  return {
    ...parsed,
    save(next) {
      localStorage.setItem(key, JSON.stringify(next));
    },
  };
}
